(() => {
  if (window.wokieeListingPageCache || !('caches' in window)) return;

  const cacheName = `wokiee-listing-pages-fast-v2-${window.theme_id || 'theme'}`;
  const cacheLifetime = 30 * 60 * 1000;
  const normalizeUrl = (url) => {
    const normalizedUrl = new URL(url, window.location.origin);
    normalizedUrl.hash = '';
    return normalizedUrl.toString();
  };

  window.wokieeListingPageCache = {
    async match(url) {
      try {
        const cache = await window.caches.open(cacheName);
        const requestUrl = normalizeUrl(url);
        const response = await cache.match(requestUrl);
        if (!response) return '';
        const cachedAt = Number.parseInt(response.headers.get('X-Wokiee-Cached-At'), 10);
        if (!cachedAt || Date.now() - cachedAt > cacheLifetime) {
          cache.delete(requestUrl);
          return '';
        }
        return await response.text();
      } catch (error) {
        return '';
      }
    },
    async put(url, html) {
      if (!html) return;
      try {
        const cache = await window.caches.open(cacheName);
        await cache.put(normalizeUrl(url), new Response(html, {
          headers: {
            'Content-Type': 'text/html; charset=utf-8',
            'X-Wokiee-Cached-At': String(Date.now()),
          },
        }));
      } catch (error) {
        // Cache Storage is an optional acceleration; network restore still works.
      }
    },
  };

  const initialGridHtml = document.getElementById('ProductGridContainer')?.outerHTML || '';
  const cacheCurrentPage = () => window.wokieeListingPageCache.put(
    window.location.pathname + window.location.search,
    initialGridHtml
  );
  if (initialGridHtml) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(cacheCurrentPage, { timeout: 1500 });
    else window.setTimeout(cacheCurrentPage, 500);
  }
})();

class InfinityButton extends HTMLElement {
  constructor() {
    super();
    this.link = this.querySelector('.btn[href]');
    this.loading = false;
    this.destroyed = false;
    this.autoscrollObserver = null;
    this.fallbackRaf = null;
    this.autoscrollVisible = false;
    this.onClickHandler = this.handleClick.bind(this);
    this.onFallbackScroll = this.queueFallbackCheck.bind(this);
    this.onIntentScroll = this.handleIntentScroll.bind(this);
    this.onUserIntent = this.handleUserIntent.bind(this);

    if (this.isTerminalLink(this.link)) this.normalizeTerminalLink(this.link);
    if (this.link) {
      this.normalizeLinkUrl(this.link);
      this.link.addEventListener('click', this.onClickHandler);
    }
  }

  connectedCallback() {
    this.destroyed = false;
    this.link = this.querySelector('.btn[href]') || this.querySelector('.btn');
    if (this.isTerminalLink(this.link)) this.normalizeTerminalLink(this.link);
    if (this.link) {
      this.normalizeLinkUrl(this.link);
      this.link.addEventListener('click', this.onClickHandler);
    }
    if (this.classList.contains('autoscroll_yes')) {
      this.bindAutoscrollIntent();
      this.observeForAutoscroll();
    }
  }

  async handleClick(event) {
    event.preventDefault();
    if (this.destroyed || this.loading) return false;

    const link = event.currentTarget;
    if (this.isTerminalLink(link)) {
      this.normalizeTerminalLink(link);
      return false;
    }
    if (!link || link.getAttribute('aria-disabled') === 'true') return false;

    this.normalizeLinkUrl(link);
    const href = link.getAttribute('href');
    if (!href) return false;

    const requestUrl = this.getRelativeUrl(href);
    window.wokieeInfinityRequests = window.wokieeInfinityRequests || new Set();
    if (window.wokieeInfinityRequests.has(requestUrl)) return false;

    const requestedPage = Number.parseInt(new URL(href, window.location.href).searchParams.get('page'), 10);
    const loadedPages = this.getLoadedPages();
    if (Number.isFinite(requestedPage) && loadedPages.has(requestedPage)) {
      const repairedUrl = new URL(href, window.location.href);
      repairedUrl.searchParams.set('page', String(Math.max(...loadedPages) + 1));
      link.setAttribute('href', repairedUrl.pathname + repairedUrl.search);
      this.observeForAutoscroll();
      return false;
    }

    const shouldRestoreAutoscroll = this.classList.contains('autoscroll_yes');
    window.wokieeInfinityRequests.add(requestUrl);
    this.loading = true;
    let loadSucceeded = false;
    this.disconnectAutoscroll();
    link.classList.add('loading');
    link.setAttribute('aria-disabled', 'true');

    const loader = document.querySelector('.collection_facets_loader');
    if (typeof placeCollectionLoader === 'function') placeCollectionLoader();
    loader?.classList.add('loading_now');

    try {
      const response = await fetch(requestUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Infinity load failed: ${response.status}`);

      const responseHtml = await response.text();
      window.wokieeListingPageCache?.put?.(requestUrl, responseHtml);
      const parsed = new DOMParser().parseFromString(responseHtml, 'text/html');
      const currentContainer = document.querySelector('#ProductGridContainer .collection__container');
      const nextContainer = parsed.querySelector('#ProductGridContainer .collection__container');
      const nextButton = parsed.querySelector('#ProductGridContainer infinity-button');

      if (!currentContainer || !nextContainer) {
        throw new Error('Infinity load failed: collection container missing');
      }

      const existingCardIds = this.getLoadedProductIds(currentContainer);
      const appendedCards = [];
      const fragment = document.createDocumentFragment();

      const nextPage = nextContainer.querySelector(':scope > wokiee-virtual-page');
      const nextItems = nextPage ? [...nextPage.children] : [...nextContainer.children];
      nextItems.forEach((item) => {
        if (item.id && existingCardIds.has(item.id)) return;
        const importedItem = document.importNode(item, true);
        if (importedItem.id) existingCardIds.add(importedItem.id);
        appendedCards.push(importedItem);
        fragment.appendChild(importedItem);
      });

      if (appendedCards.length) {
        if (window.wokieeListingWindow?.appendPage) {
          window.wokieeListingWindow.appendPage(currentContainer, appendedCards, requestedPage);
        } else {
          currentContainer.appendChild(fragment);
        }
      }
      if (Number.isFinite(requestedPage)) loadedPages.add(requestedPage);

      if (nextButton) this.replaceWith(nextButton);
      else this.remove();

      window.dispatchEvent(new CustomEvent('createProductView', {
        detail: { cards: appendedCards, container: currentContainer },
      }));
      loadSucceeded = true;
    } catch (error) {
      console.error(error);
      link.removeAttribute('aria-disabled');
    } finally {
      window.wokieeInfinityRequests.delete(requestUrl);
      this.loading = false;
      link.classList.remove('loading');
      loader?.classList.remove('loading_now');
      if (!loadSucceeded && shouldRestoreAutoscroll && this.isConnected) {
        this.observeForAutoscroll();
      }
    }

    return false;
  }

  observeForAutoscroll() {
    if (!this.link || this.destroyed || this.loading || !this.classList.contains('autoscroll_yes')) return;
    if (this.isTerminalLink(this.link)) {
      this.normalizeTerminalLink(this.link);
      return;
    }

    this.link.classList.add('autoscroll', 'hide');
    this.disconnectAutoscroll();

    if ('IntersectionObserver' in window) {
      this.autoscrollObserver = new IntersectionObserver((entries) => {
        this.autoscrollVisible = entries.some((entry) => entry.isIntersecting);
        if (this.autoscrollVisible) this.triggerAutoscroll();
      }, { root: null, rootMargin: '1200px 0px', threshold: 0 });
      this.autoscrollObserver.observe(this);
      return;
    }

    window.addEventListener('scroll', this.onFallbackScroll, { passive: true });
    window.addEventListener('resize', this.onFallbackScroll, { passive: true });
    this.queueFallbackCheck();
  }

  triggerAutoscroll() {
    if (!this.link || this.destroyed || this.loading || this.isTerminalLink(this.link)) return;
    if (!this.consumeAutoscrollIntent()) return;
    this.disconnectAutoscroll();
    this.link.classList.remove('autoscroll');
    this.link.dispatchEvent(new Event('click', { bubbles: true, cancelable: true }));
  }

  getAutoscrollState() {
    if (!window.wokieeInfinityAutoscrollState) {
      const currentY = window.scrollY || window.pageYOffset || 0;
      window.wokieeInfinityAutoscrollState = {
        armed: false,
        lastLoadScrollY: currentY,
        lastScrollY: currentY,
        userIntentUntil: 0,
      };
    }
    return window.wokieeInfinityAutoscrollState;
  }

  bindAutoscrollIntent() {
    const state = this.getAutoscrollState();
    state.lastScrollY = window.scrollY || window.pageYOffset || 0;
    window.addEventListener('scroll', this.onIntentScroll, { passive: true });
    window.addEventListener('wheel', this.onUserIntent, { passive: true });
    window.addEventListener('touchstart', this.onUserIntent, { passive: true });
    window.addEventListener('pointerdown', this.onUserIntent, { passive: true });
    window.addEventListener('keydown', this.onUserIntent);
  }

  unbindAutoscrollIntent() {
    window.removeEventListener('scroll', this.onIntentScroll);
    window.removeEventListener('wheel', this.onUserIntent);
    window.removeEventListener('touchstart', this.onUserIntent);
    window.removeEventListener('pointerdown', this.onUserIntent);
    window.removeEventListener('keydown', this.onUserIntent);
  }

  handleUserIntent(event) {
    if (event.type === 'keydown' && ![
      'ArrowDown', 'PageDown', 'End', ' ', 'Spacebar'
    ].includes(event.key)) return;
    this.getAutoscrollState().userIntentUntil = Date.now() + 2600;
  }

  handleIntentScroll() {
    const state = this.getAutoscrollState();
    const currentY = window.scrollY || window.pageYOffset || 0;
    const scrollingDown = currentY > state.lastScrollY + 0.5;
    if (scrollingDown && Date.now() <= state.userIntentUntil && currentY >= state.lastLoadScrollY + 80) {
      state.armed = true;
    }
    state.lastScrollY = currentY;
    if (state.armed && this.autoscrollVisible) this.triggerAutoscroll();
  }

  consumeAutoscrollIntent() {
    const state = this.getAutoscrollState();
    if (!state.armed) return false;
    state.armed = false;
    state.lastLoadScrollY = window.scrollY || window.pageYOffset || 0;
    state.lastScrollY = state.lastLoadScrollY;
    return true;
  }

  queueFallbackCheck() {
    if (this.fallbackRaf !== null) return;
    this.fallbackRaf = requestAnimationFrame(() => {
      this.fallbackRaf = null;
      if (this.getBoundingClientRect().top <= window.innerHeight + 1200) this.triggerAutoscroll();
    });
  }

  disconnectAutoscroll() {
    this.autoscrollObserver?.disconnect();
    this.autoscrollObserver = null;
    this.autoscrollVisible = false;
    window.removeEventListener('scroll', this.onFallbackScroll);
    window.removeEventListener('resize', this.onFallbackScroll);
    if (this.fallbackRaf !== null) cancelAnimationFrame(this.fallbackRaf);
    this.fallbackRaf = null;
  }

  normalizeLinkUrl(link) {
    if (!link) return;
    const relativeUrl = this.getRelativeUrl(link.getAttribute('href') || link.href);
    if (relativeUrl) link.setAttribute('href', relativeUrl);
  }

  getRelativeUrl(url) {
    try {
      const parsedUrl = new URL(url, window.location.href);
      return parsedUrl.pathname + parsedUrl.search;
    } catch (error) {
      return url;
    }
  }

  getListingKey() {
    const listingUrl = new URL(window.location.href);
    listingUrl.hash = '';
    listingUrl.searchParams.delete('page');
    listingUrl.searchParams.sort();
    return `${listingUrl.pathname}?${listingUrl.searchParams.toString()}`;
  }

  getLoadedPages() {
    const container = document.querySelector('#ProductGridContainer .collection__container');
    const listingKey = this.getListingKey();
    const cachedPages = window.wokieeLoadedListingPages;
    if (cachedPages?.listingKey === listingKey && cachedPages.container === container) return cachedPages.pages;

    const virtualPages = window.wokieeListingWindow?.getLoadedPages?.(container);
    const pages = virtualPages?.size ? virtualPages : new Set(
      [...(container?.querySelectorAll('.product-card[data-search-result-page]') || [])]
        .map((card) => Number.parseInt(card.dataset.searchResultPage, 10))
        .filter(Number.isFinite)
    );
    window.wokieeLoadedListingPages = { container, listingKey, pages };
    return pages;
  }

  getLoadedProductIds(currentContainer) {
    const listingKey = this.getListingKey();
    const cachedIds = window.wokieeLoadedProductIds;
    if (cachedIds?.listingKey === listingKey && cachedIds.container === currentContainer) return cachedIds.ids;

    const virtualIds = window.wokieeListingWindow?.getProductIds?.(currentContainer);
    const ids = virtualIds?.size ? virtualIds : new Set(
      [...currentContainer.querySelectorAll('.product-card[id]')].map((card) => card.id)
    );
    window.wokieeLoadedProductIds = { container: currentContainer, ids, listingKey };
    return ids;
  }

  isTerminalLink(link) {
    if (!link) return false;
    const href = (link.getAttribute('href') || '').trim();
    return !href || (
      this.classList.contains('autoscroll_yes') && link.classList.contains('mt40')
    );
  }

  normalizeTerminalLink(link) {
    if (!this.isTerminalLink(link)) return false;
    link.setAttribute('aria-disabled', 'true');
    link.removeAttribute('href');
    link.classList.remove('loading', 'autoscroll', 'hide');
    link.classList.add('mt40');
    document.querySelector('.collection_facets_loader')?.classList.remove('loading_now');
    document.getElementById('ProductGridContainer')?.classList.remove('loading_now');
    return true;
  }

  disconnectedCallback() {
    this.destroyed = true;
    this.link?.removeEventListener('click', this.onClickHandler);
    this.unbindAutoscrollIntent();
    this.disconnectAutoscroll();
  }
}

if (!customElements.get('infinity-button')) {
  customElements.define('infinity-button', InfinityButton);
}
